import React from 'react';
import { Icon } from '../primitives/Icon';

type Tone = 'primary' | 'emerald' | 'amber' | 'orange' | 'red' | 'sky';

interface MetricCardProps {
  label: string;
  value: string | number;
  icon?: string;
  tone?: Tone;
  trend?: { direction: 'up' | 'down' | 'flat'; label: string };
  hint?: string;
  onClick?: () => void;
}

const TONES: Record<Tone, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary/10',  text: 'text-primary' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700' },
  orange:  { bg: 'bg-orange-100',  text: 'text-orange-700' },
  red:     { bg: 'bg-red-100',     text: 'text-red-700' },
  sky:     { bg: 'bg-sky-100',     text: 'text-sky-700' },
};

export const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, tone = 'primary', trend, hint, onClick }) => {
  const t = TONES[tone];
  const Wrapper: any = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={[
        'p-5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900',
        'hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all text-start',
        onClick ? 'cursor-pointer' : '',
        'w-full',
      ].join(' ')}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
          {label}
        </span>
        {icon && (
          <div className={`size-9 rounded-lg ${t.bg} ${t.text} flex items-center justify-center`}>
            <Icon name={icon} size={18} />
          </div>
        )}
      </div>
      <div className="font-display text-3xl font-extrabold tabular-nums text-slate-900 dark:text-white">
        {value}
      </div>
      {trend && (
        <div
          className={[
            'text-xs font-semibold mt-2 flex items-center gap-1',
            trend.direction === 'up' ? 'text-emerald-600' :
            trend.direction === 'down' ? 'text-red-600' :
            'text-slate-500',
          ].join(' ')}
        >
          <Icon
            name={trend.direction === 'up' ? 'trending_up' : trend.direction === 'down' ? 'trending_down' : 'trending_flat'}
            size={14}
          />
          {trend.label}
        </div>
      )}
      {!trend && hint && (
        <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold mt-2">{hint}</div>
      )}
    </Wrapper>
  );
};
